// React Hook for Payroll Audit Service

import { useState, useEffect, useCallback } from 'react';
import { 
  payrollAuditService, 
  PayrollAuditAction 
} from '@/lib/payroll/audit-logging';
import { 
  PayrollPeriod, 
  PayrollAuditEntry, 
  PayrollStatus 
} from '@/types/payroll-periods';
import { useAuth } from '@/context/AuthContext';

// Hook for using payroll audit service
export const usePayrollAudit = (businessId: string) => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (businessId && user) {
      // Set audit context with user information
      payrollAuditService.setContext({
        businessId,
        userId: user.id,
        ipAddress: undefined, // Could be set from a separate service
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : undefined,
        sessionId: undefined // Could be generated or retrieved from session
      });
      setIsInitialized(true);
    }

    return () => {
      payrollAuditService.clearContext();
      setIsInitialized(false);
    };
  }, [businessId, user]);

  // Wrapper functions for common audit actions
  const logCalculation = useCallback(async (
    period: PayrollPeriod,
    employeeIds: string[],
    calculationResult: {
      totalGross: number;
      totalNet: number;
      calculationMethod: string;
      warnings?: string[];
    }
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logPayrollCalculation(period, employeeIds, calculationResult);
  }, [isInitialized]);

  const logStatusChange = useCallback(async (
    period: PayrollPeriod,
    previousStatus: PayrollStatus,
    newStatus: PayrollStatus,
    employeeIds: string[] = [],
    reason?: string
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logStatusChange(period, previousStatus, newStatus, employeeIds, reason);
  }, [isInitialized]);

  const logPaymentProcessing = useCallback(async (
    period: PayrollPeriod,
    payments: Array<{
      employeeId: string;
      amount: number;
      paymentMethod: string;
    }>,
    totalAmount: number
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logPaymentProcessing(period, payments, totalAmount);
  }, [isInitialized]);

  const logDataExport = useCallback(async (
    period: PayrollPeriod,
    exportType: 'csv' | 'pdf' | 'payslips' | 'summary',
    employeeIds: string[] = []
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logDataExport(period, exportType, employeeIds);
  }, [isInitialized]);

  const logPeriodNavigation = useCallback(async (
    fromPeriod: PayrollPeriod,
    toPeriod: PayrollPeriod
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logPeriodNavigation(fromPeriod, toPeriod);
  }, [isInitialized]);

  const logSecurityAction = useCallback(async (
    action: string,
    period?: PayrollPeriod,
    details?: Record<string, unknown>
  ) => {
    if (!isInitialized) return null;
    return payrollAuditService.logSecurityAction(action, period, details);
  }, [isInitialized]);

  const logCustomAction = useCallback(async (params: {
    action: PayrollAuditAction;
    description?: string;
    period?: PayrollPeriod;
    payrollId?: string;
    previousStatus?: PayrollStatus;
    newStatus?: PayrollStatus;
    affectedEmployees?: string[];
    actionData?: Record<string, unknown>;
    severity?: 'low' | 'medium' | 'high' | 'critical';
  }) => {
    if (!isInitialized) return null;
    return payrollAuditService.logAction(params);
  }, [isInitialized]);

  return {
    isInitialized,
    logCalculation,
    logStatusChange,
    logPaymentProcessing,
    logDataExport,
    logPeriodNavigation,
    logSecurityAction,
    logCustomAction,
    auditService: payrollAuditService
  };
};

// Hook for fetching audit logs
export const usePayrollAuditLogs = (params: {
  businessId: string;
  period?: PayrollPeriod;
  startDate?: Date;
  endDate?: Date;
  actions?: PayrollAuditAction[];
  limit?: number;
  autoRefresh?: boolean;
}) => {
  const [logs, setLogs] = useState<PayrollAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!params.businessId) return;

    setLoading(true);
    setError(null);

    try {
      const auditLogs = await payrollAuditService.getAuditLogs(params);
      setLogs(auditLogs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!params.autoRefresh) return;

    const interval = setInterval(fetchLogs, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [params.autoRefresh, fetchLogs]);

  const refresh = useCallback(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    loading,
    error,
    refresh
  };
};

// Hook for audit statistics
export const usePayrollAuditStats = (params: {
  businessId: string;
  period?: PayrollPeriod;
  days?: number;
}) => {
  const [stats, setStats] = useState<{
    totalActions: number;
    actionsByType: Record<string, number>;
    actionsByDay: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!params.businessId) return;

    setLoading(true);
    setError(null);

    try {
      const statistics = await payrollAuditService.getAuditStatistics(params);
      setStats(statistics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit statistics');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats
  };
};